import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, FlexColumn, Button, Spinner } from 'core/components';
import { Field, Form, TextArea } from 'core/form/components';
import storeLoader from 'app/stores/storeLoader';
import CommentThreadComposer from './CommentThreadComposer';

const fields = {
  comment: {
    name: 'comment',
    label: 'Comment',
    defaultValue: ''
  }
};

@inject('$comments', '$auth')
@Form({ fields, options: { name: 'AddEditComment' } })
@observer
export class AddEditCommentView extends Component {
  static defaultProps = {
    // 'card' or 'thread': a collapsed prompt with the author's avatar that opens into the comment box
    variant: 'card',
    // thread only: smaller, for narrow places like a drawer
    dense: false,
    // thread only: a rule above, separating the composer from what comes before it
    divider: false
  };

  state = {
    saving: false,
    open: false
  };

  componentDidMount() {
    const { form, initialValue } = this.props;

    if (initialValue) {
      form.setValue('comment', initialValue);
    }
  }

  handleCancelClick = () => {
    const { form, onCancel } = this.props;

    form.setValue('comment', '');
    this.setState({ open: false });

    if (onCancel) {
      onCancel();
    }
  };

  handleSave = (values) => {
    const { $auth, form, onComment, entityType, commentModel: editModel, entityId, $comments, variant } = this.props;
    const { saving } = this.state;
    const thread = variant === 'thread';
    const comment = values.comment?.trim();

    if (saving || !comment) {
      return;
    }

    this.setState({
      saving: true
    });

    // if it's edit, we update an existing model
    const request = editModel
      ? editModel.save({ comment }).then((savedComment) => {
          editModel.set(savedComment);
          form.setValue('comment', '');

          if (onComment) {
            onComment(editModel);
          }
        })
      : // otherwise, we're creating a new one
        $comments
          .saveNewComment(
            {
              comment,
              entityType,
              entityId,
              activeUser: $auth.activeUser
            },
            // In the thread the new comment shows straight away, so a success toast adds nothing.
            { toast: !thread }
          )
          .then((commentModel) => {
            form.setValue('comment', '');
            this.setState({ open: false });

            if (onComment) {
              onComment(commentModel);
            }
          });

    // api already toasts the error; the typed comment is kept so it can be retried.
    request
      .catch(() => {})
      .finally(() => {
        this.setState({
          saving: false
        });
      });
  };

  handleOpen = () => this.setState({ open: true });

  render() {
    const { saving, open } = this.state;
    const { $auth, form, editMode, large = true, variant, dense, divider } = this.props;
    const trimmedComment = form.getValue('comment').trim();
    const commentIsEmpty = trimmedComment === '';

    if (variant === 'thread') {
      return (
        <CommentThreadComposer
          userName={$auth.activeUser?.user_full_name || $auth.activeUser?.user_name || ''}
          editMode={editMode}
          open={open}
          saving={saving}
          commentIsEmpty={commentIsEmpty}
          dense={dense}
          divider={divider}
          onOpen={this.handleOpen}
          onCancel={this.handleCancelClick}
          onSubmit={() => this.handleSave(form.getValues())}
        />
      );
    }

    return (
      <FlexColumn style={{ position: 'relative' }} gap={1}>
        <Field name="comment" large={large} showLabel={false} placeholder="Add a comment..." mb={0}>
          <TextArea fill rows={large ? 1 : 2} autoFocus autoGrow style={{ maxHeight: '400px' }} />
        </Field>
        {!editMode && (
          <Button
            intent="primary"
            icon="send-message"
            disabled={commentIsEmpty}
            minimal
            style={{ position: 'absolute', right: '3px', bottom: '3px' }}
            onClick={() => this.handleSave(form.getValues())}
          />
        )}
        {editMode && (
          <Flex justifyContent="flex-end" gap={1} mb="4px">
            <Button small disabled={saving} text="Cancel" onClick={this.handleCancelClick} />
            <Button
              small
              minWidth={120}
              disabled={trimmedComment.length < 3 || saving}
              onClick={() => this.handleSave(form.getValues())}
              text={saving ? <Spinner size={24} /> : 'Save'}
              fontWeight="bold"
              intent="primary"
            />
          </Flex>
        )}
      </FlexColumn>
    );
  }
}

// Loads the company's comments first; pages that load their own entity's comments use the named export.
export default storeLoader('$comments')(AddEditCommentView);
